import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, X, ListChecks, CalendarClock, History, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ConfirmDialog from "@/components/ConfirmDialog";
import DatePicker from "@/components/DatePicker";
import MentionTextarea from "@/components/tasks/MentionTextarea";
import { WS_TASK_COMMENT_EVENT } from "@/components/NotificationBell";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { STATUS_LABEL, STATUS_COLOR, PRIORITY_COLOR, PRIORITY_LABEL, DIVISION_COLOR, fmtDateTime, fmtDate } from "@/lib/constants";
import ChecklistAssigneePicker from "@/components/tasks/ChecklistAssigneePicker";
import ChangeDeadlineDialog from "@/components/tasks/ChangeDeadlineDialog";
import DeadlineChip from "@/components/tasks/DeadlineChip";
import PriorityPicker from "@/components/tasks/PriorityPicker";
import TeamCCPicker from "@/components/tasks/TeamCCPicker";

const TRANSITIONS_ASSIGNEE = {
  todo: [
    { to: "menunggu_verifikasi", label: "Submit For Review", tone: "success" },
  ],
  in_progress: [
    { to: "menunggu_verifikasi", label: "Submit For Review", tone: "success" },
  ],
  ditolak: [
    { to: "menunggu_verifikasi", label: "Resubmit", tone: "success" },
  ],
};
const TRANSITIONS_CREATOR = {
  menunggu_verifikasi: [
    { to: "selesai", label: "Verify & Complete", primary: true },
    { to: "ditolak", label: "Perlu Revisi", tone: "warning" },
  ],
};
// Cancel is available to the creator for any non-terminal state
const CREATOR_CAN_CANCEL = new Set(["todo", "in_progress", "menunggu_verifikasi", "ditolak"]);

export default function TaskDetailDialog({ taskId, highlightCommentId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [verifyDates, setVerifyDates] = useState({ open: false, survey_date: "", sph_submit_date: "", project_done_date: "", invoice_sent_date: "" });
  const [users, setUsers] = useState([]);
  // 149-cw-r136: dialog ubah deadline.
  const [openChangeDeadline, setOpenChangeDeadline] = useState(false);
  const [showDeadlineHistory, setShowDeadlineHistory] = useState(false);

  const load = useCallback(async () => {
    if (!taskId) return;
    try {
      const [t, c, u] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/comments`),
        api.get(`/users`),
      ]);
      setTask(t.data); setComments(c.data);
      setUsers(Array.isArray(u.data) ? u.data : []);
    } catch (err) {
      if (err.response?.status === 404) {
        // Deep-link menunjuk task yg sudah dihapus / di luar scope.
        toast.info("Item tidak ditemukan — mungkin sudah dihapus atau di luar akses Anda.", { id: "deeplink-not-found" });
      } else {
        toast.error(formatDetail(err.response?.data?.detail));
      }
      onOpenChange(false);
    }
  }, [taskId, onOpenChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const changeStatus = async (to, extra = {}) => {
    setBusy(true);
    try {
      await api.patch(`/tasks/${taskId}/status`, { status: to, ...extra });
      toast.success("Status updated");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      const { data: created } = await api.post(`/tasks/${taskId}/comments`, { text: newComment.trim() });
      setNewComment("");
      // Optimistic append (WS echo di-dedup lewat id check di listener).
      setComments((prev) => (prev.some((c) => c.id === created.id) ? prev : [...prev, created]));
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const updateWODate = async (field, value) => {
    // value is "yyyy-mm-dd" from date input, or "" to clear
    const iso = value ? new Date(value).toISOString() : null;
    try {
      await api.patch(`/tasks/${taskId}/wo-dates`, { [field]: iso });
      toast.success("Tanggal diperbarui & tersync ke Working Order");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const deleteTask = async () => {
    setBusy(true);
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success("Task deleted");
      onOpenChange(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Team CC (2026-06-20): simpan perubahan daftar CC (hanya super admin / pembuat task —
  // ditegakkan juga di backend PATCH /tasks/{tid}/cc).
  const saveCC = async (ids) => {
    try {
      await api.patch(`/tasks/${taskId}/cc`, { cc_user_ids: ids });
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  if (!task) return null;

  // Route via matrix — sebelumnya hardcoded ke BOD Lv1 super-admin.
  // `canManageTask` untuk edit phase-dates (matrix `tasks.update`); `canDeleteTask` untuk tombol delete (matrix `tasks.delete`).
  const canManageTask = canPerm(user, "tasks", "update");
  const canDeleteTask = canPerm(user, "tasks", "delete");

  // Duration from creation to completion/cancellation/now (in days)
  const dayDiff = (fromIso, toIso) => {
    const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
    return Math.max(0, Math.floor(ms / 86400000));
  };
  let durationInfo;
  if (task.completed_at) {
    const d = dayDiff(task.created_at, task.completed_at);
    durationInfo = { label: "Completed In", value: `${d} day${d === 1 ? "" : "s"}`, tone: "text-emerald-700" };
  } else if (task.cancelled_at) {
    const d = dayDiff(task.created_at, task.cancelled_at);
    durationInfo = { label: "Cancelled After", value: `${d} day${d === 1 ? "" : "s"}`, tone: "text-slate-500" };
  } else {
    const d = dayDiff(task.created_at, new Date().toISOString());
    durationInfo = { label: "Elapsed", value: `${d} day${d === 1 ? "" : "s"}`, tone: "text-slate-700" };
  }

  const isAssignee = user.id === task.assignee_id;
  const isCreator = user.id === task.creator_id;
  // Team CC (2026-06-20): kelola CC hanya super admin (proxy settings.update) atau pembuat task.
  const canManageCC = isCreator || canPerm(user, "settings", "update");
  const canEditPhaseDates = (isAssignee || isCreator || canManageTask) && task.status !== "dibatalkan";
  const toDateInputVal = (iso) => (iso ? iso.slice(0, 10) : "");
  const options = [
    ...(isAssignee ? (TRANSITIONS_ASSIGNEE[task.status] || []) : []),
    ...(isCreator ? (TRANSITIONS_CREATOR[task.status] || []) : []),
    ...(isCreator && CREATOR_CAN_CANCEL.has(task.status)
      ? [{ to: "dibatalkan", label: "Cancel Task", danger: true, confirm: true }]
      : []),
  ];

  const handleClick = (o) => {
    if (o.confirm && !window.confirm("Are you sure you want to cancel this task? This cannot be undone.")) return;
    // WO phase verification → prompt for the phase dates first
    if (o.to === "selesai" && task.wo_phase) {
      setVerifyDates({
        open: true,
        survey_date: task.survey_date ? task.survey_date.slice(0, 10) : "",
        sph_submit_date: task.sph_submit_date ? task.sph_submit_date.slice(0, 10) : "",
        project_done_date: task.project_done_date ? task.project_done_date.slice(0, 10) : "",
        invoice_sent_date: task.invoice_sent_date ? task.invoice_sent_date.slice(0, 10) : "",
      });
      return;
    }
    changeStatus(o.to);
  };

  const submitVerifyDates = () => {
    const phase = task.wo_phase;
    const toIso = (d) => new Date(d).toISOString();
    const extra = {};
    if (phase === "survey_sph") {
      if (!verifyDates.survey_date || !verifyDates.sph_submit_date) {
        toast.error("Site Survey Date dan SPH Submit Date wajib diisi");
        return;
      }
      extra.survey_date = toIso(verifyDates.survey_date);
      extra.sph_submit_date = toIso(verifyDates.sph_submit_date);
    } else if (phase === "project") {
      if (!verifyDates.project_done_date) {
        toast.error("Project Done Date wajib diisi");
        return;
      }
      extra.project_done_date = toIso(verifyDates.project_done_date);
    } else if (phase === "invoice") {
      if (!verifyDates.invoice_sent_date) {
        toast.error("Invoice Sent Date wajib diisi");
        return;
      }
      extra.invoice_sent_date = toIso(verifyDates.invoice_sent_date);
    }
    changeStatus("selesai", extra);
    setVerifyDates({ open: false, survey_date: "", sph_submit_date: "", project_done_date: "", invoice_sent_date: "" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-task-detail">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[task.assignee_division]}`}>{task.assignee_division}</span>
                <span className={`px-1.5 py-0.5 text-[10px] font-semibold border ${STATUS_COLOR[task.status]}`}>{STATUS_LABEL[task.status]}</span>
                <PriorityPicker
                  task={task}
                  canEdit={(isCreator || isAssignee || canManageTask) && task.status !== "selesai" && task.status !== "dibatalkan"}
                  onChanged={() => { load(); onChanged?.(); }}
                />
              </div>
              <DialogTitle className="font-display text-2xl tracking-tight leading-tight">{task.title}</DialogTitle>
            </div>
            {canDeleteTask && (
              <button
                data-testid="btn-delete-task"
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
                title="Delete task (matrix: tasks.delete)"
                className="mt-1 text-rose-500 hover:text-rose-700 border border-rose-200 hover:border-rose-400 p-1.5 disabled:opacity-40"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-3 text-xs border border-slate-200 p-4 bg-slate-50" data-testid="task-summary">
          <div className="min-w-0">
            <div className="overline text-slate-500">Created By</div>
            <div className="mt-1 font-medium truncate" title={task.creator_name}>{task.creator_name}</div>
            <div className="text-[10px] text-slate-500 truncate">{task.creator_division} · {task.creator_sub_division}</div>
          </div>
          <div className="min-w-0">
            <div className="overline text-slate-500">Assignee</div>
            <div className="mt-1 font-medium truncate" title={task.assignee_name}>{task.assignee_name}</div>
            <div className="text-[10px] text-slate-500 truncate">{task.assignee_division} · {task.assignee_sub_division}</div>
          </div>
          <div className="min-w-0">
            <div className="overline text-slate-500">Created</div>
            <div className="mt-1 tabular-nums whitespace-nowrap">{fmtDate(task.created_at)}</div>
          </div>
          <div className="min-w-0">
            <div className="overline text-slate-500 flex items-center justify-between gap-2">
              <span>Deadline</span>
              {(isCreator || isAssignee || canManageTask) && task.status !== "selesai" && task.status !== "dibatalkan" && (
                <button
                  type="button"
                  onClick={() => setOpenChangeDeadline(true)}
                  data-testid="btn-change-deadline"
                  className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-semibold text-[#0B1D3A] hover:underline normal-case tracking-normal"
                  title="Ubah deadline dgn alasan"
                >
                  <CalendarClock size={10} /> Ubah
                </button>
              )}
            </div>
            <div className="mt-1 tabular-nums whitespace-nowrap flex items-center gap-1.5">
              <span>{fmtDate(task.deadline)}</span>
              <DeadlineChip deadline={task.deadline} status={task.status} />
            </div>
            {(task.deadline_history?.length || 0) > 0 && (
              <button
                type="button"
                onClick={() => setShowDeadlineHistory((v) => !v)}
                data-testid="btn-toggle-deadline-history"
                className="text-[10px] text-slate-500 hover:text-[#0B1D3A] flex items-center gap-0.5 mt-0.5"
              >
                <History size={9} /> {task.deadline_history.length}× diubah
              </button>
            )}
          </div>
          <div className="min-w-0" data-testid="task-duration">
            <div className="overline text-slate-500">{durationInfo.label}</div>
            <div className={`mt-1 tabular-nums font-semibold whitespace-nowrap ${durationInfo.tone}`}>{durationInfo.value}</div>
          </div>
        </div>

        {/* Team CC (2026-06-20): follower task — otomatis menerima notif setiap update/perubahan status.
            Kelola (tambah/hapus/ubah) hanya untuk super admin atau pembuat task. */}
        <div className="border border-slate-200 p-4 text-xs" data-testid="task-team-cc">
          <div className="overline text-slate-500 flex items-center gap-1.5 mb-2">
            <Users size={11} /> Team CC
            <span className="normal-case text-slate-400 font-normal">— mengikuti task & menerima notifikasi setiap update/perubahan status</span>
          </div>
          <TeamCCPicker
            users={users}
            value={task.cc_user_ids || []}
            excludeIds={[task.creator_id, task.assignee_id].filter(Boolean)}
            disabled={!canManageCC}
            onChange={saveCC}
            testid="task-cc"
          />
        </div>

        {(task.project_name || task.parent_project_name || task.project_type === "working_order") && (
          <div
            className="grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-3 text-xs border border-slate-200 p-4"
            data-testid="task-project-info"
          >
            <div className="min-w-0 md:col-span-2">
              <div className="overline text-slate-500">Project</div>
              <div className="mt-1 font-semibold text-[#0B1D3A] truncate" title={task.parent_project_name || task.project_name}>
                {task.parent_project_name || task.project_name || "—"}
              </div>
              {task.project_type === "working_order" && (
                <div className="text-[10px] uppercase tracking-wider text-indigo-700 mt-0.5">
                  Working Order{task.wo_phase ? ` · ${({ survey_sph: "Survey & SPH", project: "Project", invoice: "Invoice" }[task.wo_phase]) || task.wo_phase}` : ""}
                </div>
              )}
              {task.project_type === "variation_order" && (
                <div className="text-[10px] uppercase tracking-wider text-fuchsia-700 mt-0.5">
                  Variation Order{task.variation_order_name ? ` · ${task.variation_order_name}` : ""}
                </div>
              )}
            </div>
            {task.project_type === "working_order" && (
              <>
                <div className="min-w-0">
                  <div className="overline text-slate-500">Nomor WO</div>
                  <div className="mt-1 font-medium truncate" title={task.wo_number || ""}>{task.wo_number || "—"}</div>
                </div>
                <div className="min-w-0">
                  <div className="overline text-slate-500">Tanggal WO</div>
                  <div className="mt-1 tabular-nums whitespace-nowrap">{task.wo_date ? fmtDate(task.wo_date) : "—"}</div>
                </div>
                <div className="min-w-0">
                  <div className="overline text-slate-500">Nomor PO</div>
                  <div className="mt-1 font-medium truncate" title={task.po_number || ""}>{task.po_number || "—"}</div>
                </div>
                <div className="min-w-0 md:col-start-3">
                  <div className="overline text-slate-500">Tanggal PO</div>
                  <div className="mt-1 tabular-nums whitespace-nowrap">{task.po_date ? fmtDate(task.po_date) : "—"}</div>
                </div>
                {task.wo_phase === "survey_sph" && (
                  <>
                    <div className="min-w-0" data-testid="phase-survey-date">
                      <div className="overline text-slate-500">Site Survey Date <span className="normal-case text-[9px] text-slate-400">(auto-sync)</span></div>
                      {canEditPhaseDates ? (
                        <DatePicker
                          testid="input-survey-date"
                          value={toDateInputVal(task.survey_date)}
                          onChange={(v) => updateWODate("survey_date", v)}
                          className="mt-1"
                          placeholder="Klik pilih tanggal…"
                        />
                      ) : (
                        <div className={`mt-1 tabular-nums whitespace-nowrap font-semibold ${task.survey_date ? "text-emerald-700" : "text-slate-400"}`}>
                          {task.survey_date ? fmtDate(task.survey_date) : "Belum diisi"}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0" data-testid="phase-sph-date">
                      <div className="overline text-slate-500">SPH Submit Date <span className="normal-case text-[9px] text-slate-400">(auto-sync)</span></div>
                      {canEditPhaseDates ? (
                        <DatePicker
                          testid="input-sph-date"
                          value={toDateInputVal(task.sph_submit_date)}
                          onChange={(v) => updateWODate("sph_submit_date", v)}
                          className="mt-1"
                          placeholder="Klik pilih tanggal…"
                        />
                      ) : (
                        <div className={`mt-1 tabular-nums whitespace-nowrap font-semibold ${task.sph_submit_date ? "text-emerald-700" : "text-slate-400"}`}>
                          {task.sph_submit_date ? fmtDate(task.sph_submit_date) : "Belum diisi"}
                        </div>
                      )}
                    </div>
                  </>
                )}
                {task.wo_phase === "project" && (
                  <div className="min-w-0" data-testid="phase-project-date">
                    <div className="overline text-slate-500">Project Done Date <span className="normal-case text-[9px] text-slate-400">(auto-sync)</span></div>
                    {canEditPhaseDates ? (
                      <DatePicker
                        testid="input-project-done-date"
                        value={toDateInputVal(task.project_done_date)}
                        onChange={(v) => updateWODate("project_done_date", v)}
                        className="mt-1"
                        placeholder="Klik pilih tanggal…"
                      />
                    ) : (
                      <div className={`mt-1 tabular-nums whitespace-nowrap font-semibold ${task.project_done_date ? "text-emerald-700" : "text-slate-400"}`}>
                        {task.project_done_date ? fmtDate(task.project_done_date) : "Belum diisi"}
                      </div>
                    )}
                  </div>
                )}
                {task.wo_phase === "invoice" && (
                  <div className="min-w-0" data-testid="phase-invoice-date">
                    <div className="overline text-slate-500">Invoice Sent Date <span className="normal-case text-[9px] text-slate-400">(auto-sync)</span></div>
                    {canEditPhaseDates ? (
                      <DatePicker
                        testid="input-invoice-sent-date"
                        value={toDateInputVal(task.invoice_sent_date)}
                        onChange={(v) => updateWODate("invoice_sent_date", v)}
                        className="mt-1"
                        placeholder="Klik pilih tanggal…"
                      />
                    ) : (
                      <div className={`mt-1 tabular-nums whitespace-nowrap font-semibold ${task.invoice_sent_date ? "text-emerald-700" : "text-slate-400"}`}>
                        {task.invoice_sent_date ? fmtDate(task.invoice_sent_date) : "Belum diisi"}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {task.description && (
          <div>
            <div className="overline text-slate-500 mb-1">Description</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{task.description}</div>
          </div>
        )}

        {showDeadlineHistory && (task.deadline_history?.length || 0) > 0 && (
          <div className="border border-slate-200 bg-slate-50" data-testid="deadline-history">
            <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wider font-bold text-[#0B1D3A] flex items-center gap-1.5">
                <History size={12} /> Riwayat Perubahan Deadline
              </div>
              <button
                type="button"
                onClick={() => setShowDeadlineHistory(false)}
                className="text-slate-400 hover:text-[#0B1D3A] text-[10px] uppercase tracking-wider font-semibold"
              >
                Tutup
              </button>
            </div>
            <ul className="divide-y divide-slate-200">
              {[...(task.deadline_history || [])].reverse().map((h) => (
                <li key={h.id} className="px-3 py-2 text-xs" data-testid={`deadline-history-${h.id}`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="tabular-nums font-semibold text-slate-700">
                      {h.from ? fmtDate(h.from) : <span className="italic text-slate-400">Kosong</span>}
                    </span>
                    <span className="text-slate-400">→</span>
                    <span className="tabular-nums font-semibold text-[#0B1D3A]">
                      {h.to ? fmtDate(h.to) : <span className="italic text-slate-400">Kosong</span>}
                    </span>
                  </div>
                  <div className="text-slate-600 leading-relaxed whitespace-pre-wrap"><span className="italic text-slate-500">Alasan:</span> {h.reason}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{h.by_name} · {fmtDateTime(h.at)}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <ChecklistSection task={task} user={user} canPerm={canPerm} users={users} onChanged={() => { load(); onChanged?.(); }} />

        {options.length > 0 && (
          <div className="flex gap-2 flex-wrap border-t border-slate-200 pt-4">
            {options.map((o) => {
              const cls = o.primary
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : o.tone === "success"
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : o.tone === "warning"
                ? "bg-amber-500 hover:bg-amber-600 text-white"
                : o.danger
                ? "bg-rose-600 hover:bg-rose-700 text-white"
                : "bg-[#0B1D3A] hover:bg-[#1a2e52] text-white";
              // Guard: assignee must fill phase-specific date before Submit For Review
              // - Project task → project_done_date
              // - Invoice task → invoice_sent_date
              const blockedByPhaseDate =
                o.to === "menunggu_verifikasi" &&
                ((task.wo_phase === "project" && !task.project_done_date) ||
                  (task.wo_phase === "invoice" && !task.invoice_sent_date));
              const missingField =
                task.wo_phase === "project"
                  ? "Project Done Date"
                  : task.wo_phase === "invoice"
                  ? "Invoice Sent Date"
                  : "";
              return (
                <Button
                  key={o.to}
                  data-testid={`btn-status-${o.to}`}
                  disabled={busy || blockedByPhaseDate}
                  onClick={() => handleClick(o)}
                  title={blockedByPhaseDate ? `Isi ${missingField} terlebih dahulu` : undefined}
                  className={`rounded-none uppercase text-xs tracking-wider font-semibold ${cls} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {o.label}
                </Button>
              );
            })}
            {(task.wo_phase === "project" || task.wo_phase === "invoice") &&
              isAssignee &&
              (task.status === "todo" || task.status === "in_progress" || task.status === "ditolak") &&
              ((task.wo_phase === "project" && !task.project_done_date) ||
                (task.wo_phase === "invoice" && !task.invoice_sent_date)) && (
                <p className="text-[10px] text-amber-700 basis-full">
                  ⚠ Isi <b>{task.wo_phase === "project" ? "Project Done Date" : "Invoice Sent Date"}</b> di atas sebelum Submit For Review.
                </p>
              )}
          </div>
        )}

        <div className="border-t border-slate-200 pt-4">
          <div className="overline text-slate-500 mb-2">History</div>
          <div className="space-y-1.5 text-xs">
            {task.history?.map((h, i) => (
              <div key={i} className="flex items-center gap-3 tabular-nums">
                <span className="text-slate-400 w-40">{fmtDateTime(h.at)}</span>
                <span className={`px-1.5 py-0.5 text-[10px] border ${STATUS_COLOR[h.status]}`}>{STATUS_LABEL[h.status]}</span>
                <span className="text-slate-600">by {h.by_name}</span>
                {h.note && <span className="text-slate-500 italic">— {h.note}</span>}
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4">
          <div className="overline text-slate-500 mb-2">Discussion ({comments.length})</div>
          <div className="space-y-3 max-h-56 overflow-y-auto">
            {comments.length === 0 && <div className="text-xs text-slate-400">No comments yet.</div>}
            {comments.map((c) => {
              const isHl = highlightCommentId && c.id === highlightCommentId;
              return (
                <div
                  key={c.id}
                  data-comment-id={c.id}
                  data-testid={`comment-row-${c.id}`}
                  ref={(el) => {
                    // 149-cz: scroll + flash highlight when deep-linked comment mounts.
                    if (el && isHl) {
                      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
                    }
                  }}
                  className={`border-l-2 pl-3 transition-colors duration-700 ${isHl ? "border-[#FF5A1F] bg-orange-50" : "border-slate-300"}`}
                >
                  <div className="text-xs text-slate-500 tabular-nums">{c.author_name} · {fmtDateTime(c.created_at)}</div>
                  <div className="text-sm mt-0.5">{c.text}</div>
                </div>
              );
            })}
          </div>
          <form onSubmit={submitComment} className="mt-3 flex gap-2">
            <div className="flex-1">
              <MentionTextarea
                testid="comment-input"
                rows={2}
                value={newComment}
                onChange={setNewComment}
                users={users}
                currentUserId={user?.id}
                placeholder="Tulis komentar… Ketik @ untuk mention rekan."
                className="rounded-none resize-none w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A1F]"
              />
              <div className="mt-1 text-[10px] text-slate-400 italic">
                Tips: ketik <span className="font-mono">@nama</span> untuk mention rekan — mereka akan dapat notifikasi prioritas.
              </div>
            </div>
            <Button data-testid="comment-submit" type="submit" className="rounded-none bg-[#0B1D3A] hover:bg-[#1a2e52] self-start">Send</Button>
          </form>
        </div>
      </DialogContent>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this task permanently?"
        description="This will remove the task and all its comments. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={() => { setConfirmDelete(false); deleteTask(); }}
        testid="confirm-del-task"
      />

      <Dialog open={verifyDates.open} onOpenChange={(o) => setVerifyDates({ ...verifyDates, open: o })}>
        <DialogContent className="rounded-none max-w-md" data-testid="dialog-verify-dates">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {task.wo_phase === "survey_sph" && "Verifikasi Survey & SPH"}
              {task.wo_phase === "project" && "Verifikasi Project Done"}
              {task.wo_phase === "invoice" && "Verifikasi Invoice Sent"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Isi tanggal aktual di bawah. Tanggal ini akan otomatis diterapkan ke Working Order pada Manajemen Proyek.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {task.wo_phase === "survey_sph" && (
              <>
                <div className="space-y-1.5">
                  <Label className="overline text-slate-600">Site Survey Date <span className="text-rose-600 normal-case">*</span></Label>
                  <DatePicker
                    testid="input-verify-survey-date"
                    value={verifyDates.survey_date}
                    onChange={(v) => setVerifyDates({ ...verifyDates, survey_date: v })}
                    placeholder="Klik pilih tanggal…"
                  />
                  <p className="text-[10px] text-slate-500">→ akan mengisi <b>Site Survey Date</b> di Working Order. Wajib diisi.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="overline text-slate-600">SPH Submit Date <span className="text-rose-600 normal-case">*</span></Label>
                  <DatePicker
                    testid="input-verify-sph-date"
                    value={verifyDates.sph_submit_date}
                    onChange={(v) => setVerifyDates({ ...verifyDates, sph_submit_date: v })}
                    placeholder="Klik pilih tanggal…"
                  />
                  <p className="text-[10px] text-slate-500">→ akan mengisi <b>SPH Submit Date</b> di Working Order. Wajib diisi.</p>
                </div>
              </>
            )}
            {task.wo_phase === "project" && (
              <div className="space-y-1.5">
                <Label className="overline text-slate-600">Project Done Date <span className="text-rose-600 normal-case">*</span></Label>
                <DatePicker
                  testid="input-verify-project-done-date"
                  value={verifyDates.project_done_date}
                  onChange={(v) => setVerifyDates({ ...verifyDates, project_done_date: v })}
                  placeholder="Klik pilih tanggal…"
                />
                <p className="text-[10px] text-slate-500">→ akan mengisi <b>Project Done Date</b> di Working Order. Wajib diisi.</p>
              </div>
            )}
            {task.wo_phase === "invoice" && (
              <div className="space-y-1.5">
                <Label className="overline text-slate-600">Invoice Sent Date <span className="text-rose-600 normal-case">*</span></Label>
                <DatePicker
                  testid="input-verify-invoice-sent-date"
                  value={verifyDates.invoice_sent_date}
                  onChange={(v) => setVerifyDates({ ...verifyDates, invoice_sent_date: v })}
                  placeholder="Klik pilih tanggal…"
                />
                <p className="text-[10px] text-slate-500">→ akan mengisi <b>Invoice Sent Date</b> di Working Order. Wajib diisi.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={() => setVerifyDates({ open: false, survey_date: "", sph_submit_date: "", project_done_date: "", invoice_sent_date: "" })}>
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="submit-verify-dates"
              disabled={
                busy ||
                (task.wo_phase === "survey_sph" && (!verifyDates.survey_date || !verifyDates.sph_submit_date)) ||
                (task.wo_phase === "project" && !verifyDates.project_done_date) ||
                (task.wo_phase === "invoice" && !verifyDates.invoice_sent_date)
              }
              onClick={submitVerifyDates}
              className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white uppercase text-xs tracking-wider font-semibold disabled:opacity-50"
            >
              Verify & Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChangeDeadlineDialog
        open={openChangeDeadline}
        onOpenChange={setOpenChangeDeadline}
        task={task}
        onChanged={() => { load(); onChanged?.(); }}
      />
    </Dialog>
  );
}


// 149-cw-r134 (2026-08-31): Checklist section pada Task Detail.
// Assignee/creator/user dgn tasks.update bisa: centang, uncheck, tambah, hapus item.
function ChecklistSection({ task, user, canPerm, users = [], onChanged }) {
  const [items, setItems] = useState(task.checklist || []);
  const [input, setInput] = useState("");
  const [newAssigneeId, setNewAssigneeId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setItems(task.checklist || []); }, [task.checklist]);

  const canManage = (
    user?.id === task.creator_id ||
    user?.id === task.assignee_id ||
    canPerm(user, "tasks", "update")
  );

  const doneCount = items.filter((it) => it.done).length;
  const pct = items.length > 0 ? Math.round((doneCount / items.length) * 100) : 0;

  const toggleItem = async (item) => {
    // 149-cw-r135: item PIC juga boleh toggle item-nya sendiri (auth logic di backend).
    if (busy) return;
    // Optimistic
    setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, done: !it.done } : it));
    try {
      await api.patch(`/tasks/${task.id}/checklist/${item.id}`, { done: !item.done });
      onChanged?.();
    } catch (err) {
      // Revert
      setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, done: item.done } : it));
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal mengubah item");
    }
  };

  const addItem = async () => {
    const text = input.trim();
    if (!text || !canManage) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/checklist`, { text, assignee_id: newAssigneeId || null });
      setItems((prev) => [...prev, data]);
      setInput("");
      setNewAssigneeId(null);
      onChanged?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal menambah item");
    } finally { setBusy(false); }
  };

  const removeItem = async (item) => {
    if (!canManage || busy) return;
    setBusy(true);
    try {
      await api.delete(`/tasks/${task.id}/checklist/${item.id}`);
      setItems((prev) => prev.filter((it) => it.id !== item.id));
      onChanged?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal menghapus item");
    } finally { setBusy(false); }
  };

  const reassignItem = async (item, uid) => {
    if (!canManage) return;
    // Optimistic
    const prevA = { id: item.assignee_id, name: item.assignee_name, division: item.assignee_division };
    const newUser = uid ? users.find((u) => u.id === uid) : null;
    setItems((prev) => prev.map((it) => it.id === item.id
      ? { ...it, assignee_id: uid || null, assignee_name: newUser?.name || null, assignee_division: newUser?.division || null }
      : it));
    try {
      await api.patch(`/tasks/${task.id}/checklist/${item.id}/assign`, { assignee_id: uid || null });
      onChanged?.();
    } catch (err) {
      // Revert
      setItems((prev) => prev.map((it) => it.id === item.id
        ? { ...it, assignee_id: prevA.id, assignee_name: prevA.name, assignee_division: prevA.division }
        : it));
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal mengubah PIC");
    }
  };

  if (items.length === 0 && !canManage) return null;

  return (
    <div data-testid="task-checklist">
      <div className="overline text-slate-500 mb-1 flex items-center gap-1.5">
        <ListChecks size={12} /> Checklist / Sub-tugas
        {items.length > 0 && (
          <span className="ml-1 text-[10px] tabular-nums text-slate-500 normal-case tracking-normal">
            {doneCount}/{items.length} · {pct}%
          </span>
        )}
      </div>
      {items.length > 0 && (
        <div className="h-1 bg-slate-100 mb-2 overflow-hidden">
          <div className="h-1 bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
      <div className="border border-slate-200 divide-y divide-slate-100" data-testid="checklist-list">
        {items.length === 0 && (
          <div className="px-3 py-2 text-[11px] italic text-slate-400">Belum ada item. Tambahkan di bawah.</div>
        )}
        {items.map((it, idx) => {
          const canToggleThis = canManage || it.assignee_id === user?.id;
          return (
            <div key={it.id} className={`flex items-start gap-2 px-2 py-1.5 ${it.done ? "bg-emerald-50/40" : "bg-white"}`} data-testid={`checklist-row-${idx}`}>
              <button
                type="button"
                onClick={() => canToggleThis && toggleItem(it)}
                disabled={!canToggleThis}
                data-testid={`checklist-toggle-${idx}`}
                className={`mt-0.5 w-4 h-4 border shrink-0 inline-flex items-center justify-center transition-colors ${it.done ? "bg-emerald-500 border-emerald-500" : "border-slate-400 hover:border-[#0B1D3A]"} disabled:cursor-not-allowed disabled:opacity-60`}
                title={canToggleThis ? (it.done ? "Uncheck" : "Check") : "Hanya PIC item atau creator/assignee task yang boleh mencentang"}
                aria-label={it.done ? "Uncheck" : "Check"}
              >
                {it.done && <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`text-xs ${it.done ? "text-slate-500 line-through" : "text-slate-800"} whitespace-pre-wrap break-words`}>{it.text}</div>
                {it.done && it.done_by_name && (
                  <div className="text-[10px] text-emerald-700 mt-0.5">✓ {it.done_by_name}{it.done_at ? ` · ${fmtDateTime(it.done_at)}` : ""}</div>
                )}
              </div>
              <ChecklistAssigneePicker
                value={it.assignee_id}
                valueName={it.assignee_name}
                valueDivision={it.assignee_division}
                users={users}
                onChange={(uid) => reassignItem(it, uid)}
                disabled={!canManage}
                compact
              />
              {canManage && (
                <button
                  type="button"
                  onClick={() => removeItem(it)}
                  data-testid={`checklist-remove-${idx}`}
                  disabled={busy}
                  className="text-slate-400 hover:text-rose-600 p-1 shrink-0"
                  aria-label="Hapus item"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {canManage && (
        <div className="flex items-center gap-2 mt-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                addItem();
              }
            }}
            placeholder="Tambah item checklist…"
            data-testid="checklist-input"
            className="rounded-none flex-1 h-8 text-xs"
            disabled={busy}
          />
          <ChecklistAssigneePicker
            value={newAssigneeId}
            users={users}
            onChange={(uid) => setNewAssigneeId(uid)}
            compact
          />
          <Button
            type="button"
            onClick={addItem}
            disabled={!input.trim() || busy}
            data-testid="checklist-add-btn"
            className="rounded-none bg-[#0B1D3A] hover:bg-[#122a4d] text-white h-8 px-3 uppercase text-[10px] tracking-wider font-semibold disabled:opacity-50"
          >
            <Plus size={11} className="mr-1" /> Tambah
          </Button>
        </div>
      )}
    </div>
  );
}

